import React, { useEffect, useState } from 'react';
import { 
  StyleSheet, 
  Text, 
  View, 
  FlatList, 
  TouchableOpacity, 
  RefreshControl,
  Alert,
  ScrollView
} from 'react-native';
import { schedulesServices } from '../../../database/schedules.database';
import { FORM_SCHEDULE_STATUS } from '../stores/constants/constants.stores';

interface FormAnswerItem {
  _id?: string | number;
  answers_count?: number;
  answers_preview?: any[];
  name_form?: string;
  status?: number;
  status_send?: number;
  form_id?: string | number;
  store_id?: string | number;
  schedule_id?: string | number;
  date_init?: string;
  date_finish?: string;
  store_name?: string;
  store_code?: string;
  store_address?: string;
}

const FormsAnswersScreen = () => {
  const [formsData, setFormsData] = useState<FormAnswerItem[]>([]);
  const [filteredData, setFilteredData] = useState<FormAnswerItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedStore, setSelectedStore] = useState<string>('all');
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');
  const [stores, setStores] = useState<{store_id: string | number, store_name?: string, store_code?: string}[]>([]);

  const loadFormsData = async () => {
    try {
      const data = await schedulesServices.getAllFormsAnswers(100);
    //   console.log('FormsAnswersScreen - Datos recibidos:', data);
    //   console.log('FormsAnswersScreen - Primer elemento:', data[0]);
      setFormsData(data);
      
      // Extraer todas las tiendas únicas con su información completa
      const uniqueStores = data.reduce((acc, item) => {
        if (item.store_id && !acc.find(store => store.store_id === item.store_id)) {
          acc.push({
            store_id: item.store_id,
            store_name: item.store_name,
            store_code: item.store_code
          });
        }
        return acc;
      }, []);
      
      setStores(uniqueStores);
      
    } catch (error) {
      console.error('Error loading forms data:', error);
      Alert.alert('Error', 'No se pudieron cargar los formularios');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const getFilterDescription = () => {
    const problematicCount = formsData.filter(item => 
      (item.status === FORM_SCHEDULE_STATUS.COMPLETED || item.status_send === FORM_SCHEDULE_STATUS.COMPLETED) && 
      (item.answers_count === 0 || !item.answers_count)
    ).length;

    if (selectedStore === 'all') {
      const withStore = formsData.filter(item => item.store_id).length;
      const withoutStore = formsData.filter(item => !item.store_id).length;
      return `Mostrando ${filteredData.length} de ${formsData.length} formularios (${withStore} con tienda, ${withoutStore} sin tienda${problematicCount > 0 ? `, 🚨 ${problematicCount} problemáticos` : ''})`;
    } else if (selectedStore === 'no_store') {
      return `Mostrando ${filteredData.length} formularios sin tienda`;
    } else if (selectedStore === 'problematic') {
      return `🚨 Mostrando ${filteredData.length} formularios PROBLEMÁTICOS (completados sin respuestas)`;
    } else {
      const selectedStoreInfo = stores.find(store => store.store_id.toString() === selectedStore);
      const storeName = selectedStoreInfo?.store_code || selectedStoreInfo?.store_name || selectedStore;
      return `Mostrando ${filteredData.length} formularios de tienda: ${storeName}`;
    }
  };

  const isProblematicForm = (item: FormAnswerItem) => {
    return (item.status === FORM_SCHEDULE_STATUS.COMPLETED || item.status_send === FORM_SCHEDULE_STATUS.COMPLETED) && 
           (item.answers_count === 0 || !item.answers_count);
  };

  const applyFilters = () => {
    let filtered = [...formsData];
    
    // Filtrar por tienda
    if (selectedStore === 'no_store') {
      // Filtrar formularios sin store_id
      filtered = filtered.filter(item => !item.store_id || item.store_id === null || item.store_id === '');
    } else if (selectedStore === 'problematic') {
      // Filtrar formularios completados pero sin respuestas (PROBLEMÁTICOS)
      filtered = filtered.filter(item => 
        (item.status === FORM_SCHEDULE_STATUS.COMPLETED || item.status_send === FORM_SCHEDULE_STATUS.COMPLETED) && 
        (item.answers_count === 0 || !item.answers_count)
      );
    } else if (selectedStore !== 'all') {
      // Filtrar por tienda específica
      filtered = filtered.filter(item => item.store_id?.toString() === selectedStore);
    }
    
    // Ordenar por fecha
    filtered.sort((a, b) => {
      const dateA = new Date(a.date_init || 0).getTime();
      const dateB = new Date(b.date_init || 0).getTime();
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });
    
    setFilteredData(filtered);
  };

  useEffect(() => {
    applyFilters();
  }, [formsData, selectedStore, sortOrder]);

  useEffect(() => {
    loadFormsData();
  }, []);

  const onRefresh = () => {
    setRefreshing(true);
    loadFormsData();
  };

  const showFormDetails = (item: FormAnswerItem) => {
    const isProblematic = isProblematicForm(item);
    const details = `
${isProblematic ? '🚨 FORMULARIO PROBLEMÁTICO 🚨\n' : ''}ID: ${item._id || 'N/A'}
Nombre: ${item.name_form || 'Sin nombre'}
Cantidad de respuestas: ${item.answers_count || 0}${isProblematic ? ' ⚠️ CERO RESPUESTAS EN FORMULARIO COMPLETADO' : ''}
Status: ${getStatusText(item.status || 0)} (${item.status || 0})
Status Send: ${getStatusText(item.status_send || 0)} (${item.status_send || 0})
Form ID: ${item.form_id || 'N/A'}
Store ID: ${item.store_id || 'N/A'}
Store Name: ${item.store_name || 'N/A'}
Store Code: ${item.store_code || 'N/A'}
Store Address: ${item.store_address || 'N/A'}
Schedule ID: ${item.schedule_id || 'N/A'}
Fecha inicio: ${item.date_init ? new Date(item.date_init).toLocaleString() : 'Sin fecha'}
Fecha fin: ${item.date_finish ? new Date(item.date_finish).toLocaleString() : 'Sin fecha'}
Respuestas preview: ${item.answers_preview ? JSON.stringify(item.answers_preview, null, 2) : 'Sin datos'}
    `.trim();

    Alert.alert(
      isProblematic ? '🚨 Formulario Problemático' : 'Detalles del Formulario',
      details,
      [{ text: 'Cerrar', style: 'cancel' }],
      { cancelable: true }
    );
  };

  const getStatusColor = (status: number) => {
    switch (status) {
      case FORM_SCHEDULE_STATUS.PENDING:
        return '#FFA500'; // Orange
      case FORM_SCHEDULE_STATUS.IN_PROGRESS:
        return '#007AFF'; // Blue
      case FORM_SCHEDULE_STATUS.COMPLETED:
        return '#34C759'; // Green
      case FORM_SCHEDULE_STATUS.PENDING_CONNECTION:
        return '#FF9500'; // Dark Orange
      case FORM_SCHEDULE_STATUS.PENDING_SYNC_IMAGE:
        return '#AF52DE'; // Purple
      default:
        return '#8E8E93'; // Gray
    }
  };

  const getStatusText = (status: number) => {
    switch (status) {
      case FORM_SCHEDULE_STATUS.PENDING:
        return 'PENDING';
      case FORM_SCHEDULE_STATUS.IN_PROGRESS:
        return 'IN_PROGRESS';
      case FORM_SCHEDULE_STATUS.COMPLETED:
        return 'COMPLETED';
      case FORM_SCHEDULE_STATUS.PENDING_CONNECTION:
        return 'PENDING_CONN';
      case FORM_SCHEDULE_STATUS.PENDING_SYNC_IMAGE:
        return 'PENDING_SYNC';
      default:
        return `STATUS_${status}`;
    }
  };

  const renderFilterSection = () => (
    <View style={styles.filterContainer}>
      {/* Filtro por Tienda */}
      <View style={styles.filterGroup}>
        <Text style={styles.filterLabel}>Tienda:</Text>
        <ScrollView 
          horizontal 
          showsHorizontalScrollIndicator={false}
          style={styles.horizontalScroll}
        >
          <TouchableOpacity
            style={[
              styles.filterButton,
              selectedStore === 'all' && styles.filterButtonActive
            ]}
            onPress={() => setSelectedStore('all')}
          >
            <Text style={[
              styles.filterButtonText,
              selectedStore === 'all' && styles.filterButtonTextActive
            ]}>
              Todas
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[
              styles.filterButton,
              styles.noStoreButton,
              selectedStore === 'no_store' && styles.filterButtonActive
            ]}
            onPress={() => setSelectedStore('no_store')}
          >
            <Text style={[
              styles.filterButtonText,
              selectedStore === 'no_store' && styles.filterButtonTextActive
            ]}>
              Sin tienda
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[
              styles.filterButton,
              styles.problematicButton,
              selectedStore === 'problematic' && styles.filterButtonActive
            ]}
            onPress={() => setSelectedStore('problematic')}
          >
            <Text style={[
              styles.filterButtonText,
              selectedStore === 'problematic' && styles.filterButtonTextActive
            ]}>
              🚨 Problemáticos
            </Text>
          </TouchableOpacity>
          
          {stores.map((store) => (
            <TouchableOpacity
              key={store.store_id}
              style={[
                styles.filterButton,
                selectedStore === store.store_id.toString() && styles.filterButtonActive
              ]}
              onPress={() => setSelectedStore(store.store_id.toString())}
            >
              <Text style={[
                styles.filterButtonText,
                selectedStore === store.store_id.toString() && styles.filterButtonTextActive
              ]}>
                {store.store_code || store.store_name || store.store_id.toString().slice(-8)}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* Filtro por Orden */}
      <View style={styles.filterGroup}>
        <Text style={styles.filterLabel}>Orden:</Text>
        <View style={styles.orderButtonsContainer}>
          <TouchableOpacity
            style={[
              styles.orderButton,
              styles.orderButtonLeft,
              sortOrder === 'desc' && styles.orderButtonActive
            ]}
            onPress={() => setSortOrder('desc')}
          >
            <Text style={[
              styles.orderButtonText,
              sortOrder === 'desc' && styles.orderButtonTextActive
            ]}>
              Más recientes
            </Text>
          </TouchableOpacity>
          
          <TouchableOpacity
            style={[
              styles.orderButton,
              styles.orderButtonRight,
              sortOrder === 'asc' && styles.orderButtonActive
            ]}
            onPress={() => setSortOrder('asc')}
          >
            <Text style={[
              styles.orderButtonText,
              sortOrder === 'asc' && styles.orderButtonTextActive
            ]}>
              Más antiguos
            </Text>
          </TouchableOpacity>
        </View>
      </View>
      
      <Text style={styles.resultCount}>
        {getFilterDescription()}
      </Text>
    </View>
  );

  const renderFormItem = ({ item }: { item: FormAnswerItem }) => (
    <TouchableOpacity
      style={[
        styles.itemContainer,
        isProblematicForm(item) && styles.problematicItemContainer
      ]}
      onPress={() => showFormDetails(item)}
      activeOpacity={0.7}
    >
      {isProblematicForm(item) && (
        <View style={styles.problematicBadge}>
          <Text style={styles.problematicBadgeText}>🚨 PROBLEMÁTICO</Text>
        </View>
      )}
      
      <View style={styles.row}>
        <Text style={styles.idText} numberOfLines={1}>
          {item._id ? item._id.toString().slice(-8) : 'N/A'}
        </Text>
        
        <View style={styles.answersContainer}>
          <Text style={[
            styles.answersCount, 
            isProblematicForm(item) && styles.problematicAnswersCount
          ]}>
            {item.answers_count || 0}
          </Text>
          <Text style={styles.answersLabel}>answers</Text>
        </View>
        
        <Text style={styles.nameText} numberOfLines={1}>
          {item.name_form || 'Sin nombre'}
        </Text>
        
        <View style={styles.statusContainer}>
          <View style={[styles.statusBadge, { backgroundColor: getStatusColor(item.status || 0) }]}>
            <Text style={styles.statusText}>
              {getStatusText(item.status || 0)}
            </Text>
          </View>
        </View>
        
        <View style={styles.statusContainer}>
          <View style={[styles.statusBadge, { backgroundColor: getStatusColor(item.status_send || 0) }]}>
            <Text style={styles.statusText}>
              {getStatusText(item.status_send || 0)}
            </Text>
          </View>
        </View>
      </View>
      
      <View style={styles.detailsRow}>
        <Text style={styles.detailText}>Form: {item.form_id ? item.form_id.toString().slice(-8) : 'N/A'}</Text>
        <Text style={styles.detailText}>
          Store: {item.store_id ? 
            (item.store_code || item.store_name || item.store_id.toString().slice(-8)) : 
            '🚫 Sin tienda'
          }
        </Text>
        <Text style={styles.detailText}>
          {item.date_init ? new Date(item.date_init).toLocaleDateString() : 'Sin fecha'}
        </Text>
      </View>
      
      <View style={styles.tapHint}>
        <Text style={styles.tapHintText}>👆 Toca para ver detalles</Text>
      </View>
    </TouchableOpacity>
  );

  const renderHeader = () => (
    <View style={styles.headerContainer}>
      <Text style={styles.headerText}>ID</Text>
      <Text style={styles.headerText}>Answers</Text>
      <Text style={styles.headerText}>Nombre</Text>
      <Text style={styles.headerText}>Status</Text>
      <Text style={styles.headerText}>Status Send</Text>
    </View>
  );

  if (loading) {
    return (
      <View style={styles.centerContainer}>
        <Text>Cargando formularios...</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.headerSection}>
        <View style={styles.titleContainer}>
          <Text style={styles.title}>Formularios Guardados</Text>
          <Text style={styles.subtitle}>Total: {formsData.length} registros</Text>
        </View>
        
        {renderFilterSection()}
        {renderHeader()}
      </View>
      
      <FlatList
        data={filteredData}
        renderItem={renderFormItem}
        keyExtractor={(item, index) => item._id ? item._id.toString() : index.toString()}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
        ListEmptyComponent={
          <View style={styles.centerContainer}>
            <Text style={styles.emptyText}>No hay formularios guardados</Text>
          </View>
        }
        contentContainerStyle={styles.listContainer}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
    padding: 16,
  },
  titleContainer: {
    marginBottom: 16,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333',
  },
  subtitle: {
    fontSize: 14,
    color: '#666',
    marginTop: 4,
  },
  filterContainer: {
    backgroundColor: 'white',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
    zIndex: 1000,
    elevation: 5,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
  },
  filterGroup: {
    marginBottom: 16,
  },
  filterLabel: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 8,
  },
  horizontalScroll: {
    flexDirection: 'row',
  },
  filterButton: {
    backgroundColor: '#f0f0f0',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
    marginRight: 8,
    borderWidth: 1,
    borderColor: '#ddd',
  },
  filterButtonActive: {
    backgroundColor: '#007AFF',
    borderColor: '#007AFF',
  },
  filterButtonText: {
    fontSize: 12,
    color: '#333',
    fontWeight: '500',
  },
  filterButtonTextActive: {
    color: 'white',
  },
  noStoreButton: {
    backgroundColor: '#FFF3CD',
    borderColor: '#FFEAA7',
  },
  problematicButton: {
    backgroundColor: '#FFEBEE',
    borderColor: '#FFCDD2',
  },
  problematicItemContainer: {
    borderLeftWidth: 4,
    borderLeftColor: '#F44336',
    backgroundColor: '#FFEBEE',
  },
  problematicBadge: {
    position: 'absolute',
    top: 8,
    right: 8,
    backgroundColor: '#F44336',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    zIndex: 1,
  },
  problematicBadgeText: {
    color: 'white',
    fontSize: 8,
    fontWeight: 'bold',
  },
  problematicAnswersCount: {
    color: '#F44336',
  },
  orderButtonsContainer: {
    flexDirection: 'row',
    borderRadius: 8,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#ddd',
  },
  orderButton: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: '#f9f9f9',
    alignItems: 'center',
  },
  orderButtonLeft: {
    borderTopLeftRadius: 8,
    borderBottomLeftRadius: 8,
  },
  orderButtonRight: {
    borderTopRightRadius: 8,
    borderBottomRightRadius: 8,
    borderLeftWidth: 1,
    borderLeftColor: '#ddd',
  },
  orderButtonActive: {
    backgroundColor: '#007AFF',
  },
  orderButtonText: {
    fontSize: 14,
    color: '#333',
    fontWeight: '500',
  },
  orderButtonTextActive: {
    color: 'white',
  },
  resultCount: {
    fontSize: 12,
    color: '#666',
    textAlign: 'center',
    fontStyle: 'italic',
  },
  headerContainer: {
    flexDirection: 'row',
    backgroundColor: '#e0e0e0',
    paddingVertical: 12,
    paddingHorizontal: 8,
    borderRadius: 8,
    marginBottom: 8,
  },
  headerText: {
    flex: 1,
    fontWeight: 'bold',
    fontSize: 12,
    color: '#333',
    textAlign: 'center',
  },
  itemContainer: {
    backgroundColor: 'white',
    marginBottom: 8,
    borderRadius: 8,
    padding: 12,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.22,
    shadowRadius: 2.22,
    elevation: 3,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  idText: {
    flex: 1,
    fontSize: 12,
    color: '#333',
    textAlign: 'center',
  },
  answersContainer: {
    flex: 1,
    alignItems: 'center',
  },
  answersCount: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#007AFF',
  },
  answersLabel: {
    fontSize: 10,
    color: '#666',
  },
  nameText: {
    flex: 1,
    fontSize: 12,
    color: '#333',
    textAlign: 'center',
  },
  statusContainer: {
    flex: 1,
    alignItems: 'center',
  },
  statusBadge: {
    paddingVertical: 4,
    paddingHorizontal: 6,
    borderRadius: 12,
    minWidth: 60,
    alignItems: 'center',
  },
  statusText: {
    fontSize: 10,
    fontWeight: 'bold',
    color: 'white',
  },
  detailsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 4,
  },
  detailText: {
    fontSize: 10,
    color: '#666',
    flex: 1,
    textAlign: 'center',
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
  },
  tapHint: {
    marginTop: 4,
    alignItems: 'center',
  },
  tapHintText: {
    fontSize: 10,
    color: '#999',
    fontStyle: 'italic',
  },
  headerSection: {
    backgroundColor: '#f5f5f5',
    zIndex: 10,
  },
  listContainer: {
    flexGrow: 1,
  },
});

// Exportaciones para mayor compatibilidad
export default FormsAnswersScreen;
export { FormsAnswersScreen };
